import { z } from "zod";
/**
 * Task 10.1 — *I am cooking this; which bottle should I open?*
 *
 * The decision is in `project-scope.md`, 2026-09-18. The useful direction is
 * from the meal to the bottle rather than the other way round: the question
 * asked in a kitchen, not the one asked by somebody already holding a bottle.
 *
 * **The response names wines by id and never by name.** The model is handed
 * the candidates and returns their ids with a reason each; the page draws the
 * row from the cellar's own record. So a wine that does not exist cannot be
 * recommended, and the shelf it stands on cannot be got wrong — which matters,
 * because the shelf is the half that makes the advice actionable before
 * dinner.
 */
/**
 * What is being cooked, as the person types it.
 *
 * Free text and Swedish, because that is what a meal is. Bounded at 200
 * characters — long enough for *oxfilé med rödvinssås och rotfrukter* and
 * short enough that the field cannot become a way to write an essay into
 * somebody else's API bill. Trimmed and non-empty, because a blank question
 * would be answered with a confident ranking of nothing in particular.
 */
export const mealSuggestionRequestSchema = z.object({
    dish: z.string().trim().min(2).max(200),
});
/**
 * One bottle the model picked, and why.
 *
 * `reason` is prose for a person standing in a kitchen: two or three sentences
 * about why this bottle and this meal, in the cellar's own language. It is
 * deliberately not a score — a number would invite sorting by it, and the
 * ranking is already the answer to *which first*.
 */
export const mealPickSchema = z.object({
    wineId: z.string(),
    reason: z.string(),
    /**
     * The paragraph the cellar described this wine in, word for word — what the
     * answer rests on as far as the cellar is concerned. Added 2026-09-27, when
     * the owner asked what the answers are based on, and required since
     * revision 56 sends it. The model looks nothing up, so this and its general
     * knowledge are the whole of it.
     *
     * Not nullable: a meal answer is never stored, so there is no pick from
     * before this existed that could lack it.
     */
    told: z.string(),
});
/**
 * The answer.
 *
 * `picks` is ordered, best first, and may be empty: a cellar of nothing but
 * Champagne asked about a rare steak should say so rather than rank the
 * Champagne. `note` is where that is said — one sentence about the whole
 * answer rather than about one bottle, and null when there is nothing to add.
 *
 * `model` travels with the answer for the reason `WineWindowSuggestion` records
 * it: which model said this is part of what the sentence is worth. It is on the
 * response rather than in a row because nothing here is stored — see the
 * decision; a pairing is advice for one evening and is not a claim about a
 * bottle that has to survive twenty years.
 */
export const mealSuggestionResponseSchema = z.object({
    picks: z.array(mealPickSchema),
    /**
     * Nullable because the model may offer nothing beyond the picks, and that
     * is an answer. The default it carried for `10.1`'s release came off on
     * 2026-09-22: a response with no `note` key at all is a broken deploy, not
     * a quiet evening.
     */
    note: z.string().nullable(),
    model: z.string(),
    /**
     * How wide the choice was: how many wines were on the list the model chose
     * from, and how many the cellar holds that were left off it because they
     * cannot be opened tonight. Added 2026-09-27 with `told`. The one release
     * they were null for an API that did not send them yet is over; a response
     * without them is now a broken deploy.
     */
    offered: z.number().int(),
    withheld: z.number().int(),
});
//# sourceMappingURL=meal.js.map